<script lang="ts" setup>
import type { ICellRendererParams } from 'ag-grid-community';
import type { PropType} from 'vue';
import { computed, ref } from 'vue';
import type { SyncData } from '@sysarb/sysarb-types/services/v1/pay-management';

type ExtendedParams = ICellRendererParams &{
    id: string;
    syncFunction: (idsToSync: string[]) => Promise<SyncData | null> | SyncData;
    title: (params: ICellRendererParams) => string;
    hide?: (params: ICellRendererParams) => boolean;
    inSync?: (params: ICellRendererParams) => boolean;
    disabled?: (params: ICellRendererParams) => boolean;
    disabledText?: (params: ICellRendererParams) => string;
};

const props = defineProps({
    params: {
        type: Object as PropType<ExtendedParams>,
        required: true
    }
});

/**********
* Init
**********/

/** Sync function */
const syncFunction = computed(() => props.params.syncFunction);

/** Title to be used on hover */
const title = computed(() => (typeof props.params.title === 'function' ? props.params.title(props.params) : props.params.title));
const disabledText = computed(() => (typeof props.params.disabledText === 'function' ? props.params.disabledText(props.params) : props.params.disabledText));
const inSync = computed(() => (typeof props.params.inSync === 'function' ? props.params.inSync(props.params) : props.params.inSync));
const disabled = computed(() => (typeof props.params.disabled === 'function' ? props.params.disabled(props.params) : props.params.disabled));

const syncing = ref(false);
const syncFailed = ref(false);
const syncSuccess = ref(false);

/**
 * Start syncing.
 */
async function startSync(): Promise<void> {
    // Reset all values
    syncing.value = true;
    syncFailed.value = false;
    syncSuccess.value = false;

    try {
        // Check if any rows are selected. If not we sync all employments that has a sync property
        await syncFunction.value([props.params.data.id]);
        // Timeout for letting the animation playout
        setTimeout(() => {
            syncing.value = false;
            syncSuccess.value = true;
        }, 1000);
    } catch (e: unknown) {
        console.error(e);
        syncFailed.value = true;
        syncing.value = false;
    }
}

const syncIcon = computed(() => {
    if (syncSuccess.value || inSync.value === true) {
        return 'check';
    }

    if (syncFailed.value) {
        return 'alert-triangle';
    }
    return 'refresh-cw';
});
</script>

<template>
    <button
        v-show="!inSync"
        v-tooltip="disabled ? disabledText : ''"
        class="sy-sync-button flex-container align-middle height-100 width-100 padding-horizontal-5"
        :title="title"
        :disabled="disabled"
        @click="startSync"
    >
        <Icon :icon="syncIcon" :class="['sy-icon-transition', { 'sy-loading' : syncing, 'sy-icon-green sy-in-sync': inSync, 'sy-icon-green sy-success': syncSuccess, 'sy-icon-red sy-fail': syncFailed }]" />
    </button>
</template>

<style lang="scss" scoped>
.sy-sync-button {
    &:hover > svg:not(.sy-success, .sy-fail, .sy-in-sync) {
        transform:rotate(20deg);
    }
}

.sy-loading {
    animation-name: spin;
    animation-duration: 2000ms;
    animation-iteration-count: infinite;
    animation-timing-function: ease;
    @keyframes spin {
        from {
            transform:rotate(20deg);
        }
        to {
            transform:rotate(360deg);
        }
    }
}

.sy-success {
    animation-name: bounce;
    animation-duration: 1000ms;
    animation-timing-function: ease;
    @keyframes bounce {
        0%   { transform: translateY(20px); }
        50%  { transform: translateY(-5px); }
        100% { transform: translateY(0); }
    }
}

.sy-fail {
    animation-name: shake;
    animation-duration: 1000ms;
    animation-timing-function: ease;
    @keyframes shake {
        10%, 90% {
            transform: translate3d(-1px, 0, 0);
        }

        20%, 80% {
            transform: translate3d(2px, 0, 0);
        }

        30%, 50%, 70% {
            transform: translate3d(-4px, 0, 0);
        }

        40%, 60% {
            transform: translate3d(4px, 0, 0);
        }
    }
}
</style>
